import type { DTOOf, ITicket } from '@kfolks/types'
import { apiClient } from './api.client'

export type TicketDTO = DTOOf<ITicket>

/**
 * Admin-only ticket-sync trigger. Mirrors `POST /tickets/sync` on the
 * backend (`ticket-sync-controller.ts`). Returns the queued job id;
 * the caller can poll `getStatus(jobId)` to watch progress.
 *
 * The endpoint is gated by `roles: [ADMIN, SUPER_ADMIN]` server-side —
 * the UI hides the trigger from non-admins, but the auth check is the
 * load-bearing one.
 */

export interface TicketSyncJobAcceptedDTO {
	jobId: string
	status: string
	message?: string
}

export interface TicketSyncJobStatusDTO {
	jobId: string
	status: 'accepted' | 'running' | 'completed' | 'failed'
	triggeredBy?: string
	startedAt?: string | null
	completedAt?: string | null
	summary?: Record<string, unknown> | null
	error?: string | null
	createdAt?: string
}

export const ticketSyncApi = {
	/** Queue an async sync job. Returns 202 + `{ jobId, status }`. */
	async trigger(): Promise<TicketSyncJobAcceptedDTO> {
		const res = await apiClient.post<TicketSyncJobAcceptedDTO>('/tickets/sync')
		return res.data
	},
	/** Poll the job's status. Returns 404 if the id is unknown. */
	async getStatus(jobId: string): Promise<TicketSyncJobStatusDTO> {
		const res = await apiClient.get<TicketSyncJobStatusDTO>(`/tickets/sync/${jobId}`)
		return res.data
	},
	/** Admin Tickets page list. Filters: `status` (open|closed). */
	async listAll(params: { status?: string; page?: number; limit?: number } = {}): Promise<{
		data: TicketDTO[]
		total: number
	}> {
		const qs = new URLSearchParams()
		if (params.status) qs.set('filters[status]', params.status)
		if (params.page) qs.set('page', String(params.page))
		if (params.limit) qs.set('limit', String(params.limit))
		const url = qs.toString() ? `/tickets?${qs.toString()}` : '/tickets'
		const res = await apiClient.get<{ data: TicketDTO[]; total: number }>(url)
		return res.data
	},
}
